import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  isSignInWithEmailLink,
  RecaptchaVerifier,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPhoneNumber,
} from "firebase/auth";
import { loadingUser } from "../../redux/actions/user";
import { fbAuth } from "../../services/firebaseService/connection";
import {
  fbGetUserByPhone,
  fbCheckDashboardAccess,
} from "../../services/firebaseService/endPoints/user";
import logger from "../../utils/logger";
import { setEmailAuthUidOverride } from "../../utils/emailAuthSession";
import useAdminDataStore, { selectRedirectPath } from "../../store/adminData/adminData";

const useLoginFlow = () => {
  const navigate = useNavigate();
  const redirectPath = useAdminDataStore(selectRedirectPath);

  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [confirmResult, setConfirmResult] = useState(null);
  const [otp, setOtp] = useState("");
  const [otpComplete, setOtpComplete] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [lastPhone, setLastPhone] = useState("");
  const [alert, setAlert] = useState({ show: false, message: "", type: "error" });
  const [emailAuthRequired, setEmailAuthRequired] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [emailAuthPhone, setEmailAuthPhone] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  // The emailed oobCode can only be consumed once — guard against processing it
  // twice (e.g. React StrictMode's dev-mode double effect invocation), since a
  // second signInWithEmailLink call with an already-used code flips auth state
  // to no-user, which wipes the override set by the first, successful run.
  const emailLinkHandledRef = useRef(false);

  const showAlert = (message, type = "error") => setAlert({ show: true, message, type });
  const clearAlert = () => setAlert({ show: false, message: "", type: "error" });

  const friendlyError = (err) => {
    if (err.code === "auth/too-many-requests")
      return "Too many attempts. Please wait 15 minutes and confirm the phone number before trying again.";
    if (err.message?.includes("reCAPTCHA"))
      return "Something went wrong. Please refresh the page and try again.";
    return err.message === "TOO_SHORT" ? "Enter a valid phone number." : err.message;
  };

  const navigateAfterLogin = useCallback(() => {
    navigate(redirectPath || "/", { replace: true });
  }, [navigate, redirectPath]);

  const handlePhoneChange = useCallback(
    (phone, code) => {
      if (confirmResult) setConfirmResult(null);
      if (emailAuthRequired) {
        setEmailAuthRequired(false);
        setEmailSent(false);
      }
      setPhoneNumber(phone);
      setCountryCode(code);
    },
    [confirmResult, emailAuthRequired]
  );

  const handleRequestOtp = useCallback(() => {
    clearAlert();

    if (!isChecked) {
      showAlert("Please accept the Terms & Conditions to continue.");
      return;
    }

    let stripped = phoneNumber;
    if (stripped.startsWith(countryCode)) stripped = stripped.substring(countryCode.length);
    if (stripped.startsWith("0")) stripped = stripped.substring(1);
    const fullPhone = `${countryCode}${stripped}`;

    setSendingOtp(true);

    fbGetUserByPhone(fullPhone)
      .then(async (userData) => {
        if (!userData?.id) {
          showAlert("no-account", "no-account");
          setSendingOtp(false);
          return null;
        }
        const canAccess = await fbCheckDashboardAccess(userData);
        if (!canAccess) {
          showAlert("Must be an admin for dashboard access.", "error");
          setSendingOtp(false);
          return null;
        }
        if (userData?.requiresEmailAuth || userData?.authEmail) {
          logger.log("[emailAuth] real uid resolved via phone lookup:", userData.id);
          setEmailAuthRequired(true);
          setUserEmail(userData.email);
          setEmailAuthPhone(fullPhone);
          setSendingOtp(false);
          return null;
        }
        return signInWithPhoneNumber(fbAuth, fullPhone, window.recaptchaVerifier);
      })
      .then((result) => {
        if (result) {
          setConfirmResult(result);
          setLastPhone(fullPhone);
          setResendCooldown(30);
          setSendingOtp(false);
        }
      })
      .catch((err) => {
        setSendingOtp(false);
        showAlert(friendlyError(err));
      });
  }, [phoneNumber, countryCode, isChecked]);

  const handleVerifyOtp = useCallback(
    (otpValue) => {
      if (verifyingOtp || !confirmResult) return;
      clearAlert();
      setVerifyingOtp(true);

      confirmResult
        .confirm(otpValue)
        .then(() => {
          setVerifyingOtp(false);
          loadingUser();
          navigateAfterLogin();
        })
        .catch(() => {
          setVerifyingOtp(false);
          setOtp("");
          setOtpComplete(false);
          showAlert("Invalid code — please check and try again.");
        });
    },
    [confirmResult, verifyingOtp, navigateAfterLogin]
  );

  const handleOtpChange = useCallback(
    (val) => {
      setOtp(val);
      const complete = val.length === 6;
      setOtpComplete(complete);
      if (complete) {
        setTimeout(() => handleVerifyOtp(val), 300);
      }
    },
    [handleVerifyOtp]
  );

  // Countdown ticker
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const resendOtp = useCallback(() => {
    if (resendCooldown > 0 || !lastPhone) return;
    clearAlert();
    setResendCooldown(30);
    signInWithPhoneNumber(fbAuth, lastPhone, window.recaptchaVerifier)
      .then((result) => setConfirmResult(result))
      .catch((err) => showAlert(friendlyError(err)));
  }, [resendCooldown, lastPhone]);

  const resetToPhone = useCallback(() => {
    setConfirmResult(null);
    setOtp("");
    setOtpComplete(false);
    clearAlert();
  }, []);

  const resetFromEmailAuth = useCallback(() => {
    setEmailAuthRequired(false);
    setEmailSent(false);
    clearAlert();
  }, []);

  const handleSendEmailLink = useCallback(() => {
    clearAlert();
    setSendingEmail(true);

    const actionCodeSettings = {
      url: `${window.location.origin}/login?email=${encodeURIComponent(userEmail)}&phone=${encodeURIComponent(emailAuthPhone)}`,
      handleCodeInApp: true,
    };

    sendSignInLinkToEmail(fbAuth, userEmail, actionCodeSettings)
      .then(() => {
        setSendingEmail(false);
        setEmailSent(true);
      })
      .catch((err) => {
        setSendingEmail(false);
        showAlert(friendlyError(err));
      });
  }, [userEmail, emailAuthPhone]);

  // Email-link sign-in (return trip after the user clicks the emailed link) — kept in
  // its own try/catch, separate from reCAPTCHA setup below, so a reCAPTCHA init
  // failure can never silently swallow the email-link handling.
  useEffect(() => {
    try {
      if (isSignInWithEmailLink(fbAuth, window.location.href)) {
        const urlParams = new URLSearchParams(window.location.search);
        const email = urlParams.get("email");
        const phone = urlParams.get("phone");
        if (email && !emailLinkHandledRef.current) {
          emailLinkHandledRef.current = true;
          setSendingOtp(true);
          // Resolve + set the real uid BEFORE calling signInWithEmailLink — that call
          // changes fbAuth.currentUser, which fires App.jsx's global onAuthStateChanged
          // listener (→ updateUser()) immediately. If the override isn't already in
          // place by then, that listener runs against the fresh, unrelated email-link
          // uid, finds no matching data, and logs the user straight back out — a race
          // that finishing the override-set afterward is too late to prevent.
          // Matched by phone (not email) — phone is this app's sole/primary identifier
          // (the whole flow only reaches email-auth after a successful phone lookup),
          // and email isn't guaranteed unique across accounts.
          fbGetUserByPhone(phone)
            .then((userData) => {
              if (!userData?.id) {
                throw new Error("No account found for this phone.");
              }
              logger.log("[emailAuth] real uid resolved via phone lookup (final, in use):", userData.id);
              setEmailAuthUidOverride(userData.id);
              return signInWithEmailLink(fbAuth, email, window.location.href);
            })
            .then((result) => {
              if (!result) return;
              logger.log("[emailAuth] uid from signInWithEmailLink (mismatched, unused):", result.user?.uid);
              setSendingOtp(false);
              loadingUser();
              navigateAfterLogin();
            })
            .catch((err) => {
              logger.log("[emailAuth] email-link sign-in failed:", err);
              setSendingOtp(false);
              showAlert("Failed to login by email link.");
            });
        }
      }
    } catch (err) {
      showAlert(friendlyError(err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // reCAPTCHA setup
  useEffect(() => {
    try {
      // Clear any leftover reCAPTCHA DOM from a previous render (e.g. React StrictMode double-invoke)
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
      const container = document.getElementById("recaptcha-container");
      if (container) container.innerHTML = "";

      window.recaptchaVerifier = new RecaptchaVerifier(fbAuth, "recaptcha-container", {
        size: "invisible",
        callback: () => {},
      });
      window.recaptchaVerifier.render().then((id) => logger.log(id));
    } catch {
      showAlert(
        "Captcha Error in Safari? Either use Chrome (advised) or in Safari Settings/Privacy uncheck 'prevent cross-site ip tracking' and 'hide ip addresses from trackers'."
      );
    }

    return () => {
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = "";
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    phoneNumber,
    isChecked,
    setIsChecked,
    confirmResult,
    otp,
    otpComplete,
    sendingOtp,
    verifyingOtp,
    alert,
    handlePhoneChange,
    handleOtpChange,
    handleRequestOtp,
    handleVerifyOtp,
    resendOtp,
    resendCooldown,
    resetToPhone,
    emailAuthRequired,
    userEmail,
    sendingEmail,
    emailSent,
    handleSendEmailLink,
    resetFromEmailAuth,
  };
};

export default useLoginFlow;
